// Site público direto do módulo de cada página, não do barrel `@/pages`:
// o barrel reexporta também a área logada, que vai sob demanda
// (src/routes/lazyPages.ts).
import { Auth, ChangePassword, ForgotPasswordCode, ForgotPasswordPhone, ForgotPasswordReset, OtpLoginCode, OtpLoginPhone } from "@/pages/auth";
import { LandingPage } from "@/pages/landingPage";
import { CobrancaWhatsapp } from "@/pages/cobrancaWhatsapp";
import { ReguaCobranca } from "@/pages/reguaCobranca";
import { Crediario } from "@/pages/crediario/crediario";
import { Newsroom } from "@/pages/newsroom/newsroom";
import { Integracoes } from "@/pages/integracoes";
import { Ferramentas, GeradorDeCarne, SimuladorDeCrediario, CalculadoraDeInadimplencia } from "@/pages/ferramentas";
import { SeoLanding, SEO_LANDINGS } from "@/pages/seoLanding";
import { Alpha7 } from "@/pages/alpha7";
import { PoliticaDePrivacidade, Lgpd, RetencaoDeDados, Seguranca, ComoAFidexaContaPagamentos, Disponibilidade, PoliticaDeCancelamento, Incidente20261016, Ouvidoria, Contato, TermosDeUso } from "@/pages/legal";
import { NotFound } from "@/pages/notFound/notFound";
import { Suspense } from "react";
import { lazyWithReload } from "@/utils/lazyWithReload";
import {
  BillingManagement,
  CsatReport,
  Customers,
  Dashboard,
  DashboardChangePassword,
  FidexaPayAccount,
  FidexaPaySettings,
  FidexaPayStatements,
  FidexaPayWithdraw,
  ImportSheets,
  ImportSheetsItems,
  MessageManager,
  ReceivablesPaid,
  Roadmap,
  ScheduledClients,
  SendMessages,
  Settings,
  Training,
  TrainingDetail,
  TrainingRanking,
  UnresponsiveClients,
  User,
  BillingResultDetailPage,
  BillingResultsPage,
  BillingQueuePage,
  CreditCustomerPage,
  FidexaNotificationPage,
  CampaignResultDetailPage,
  CampaignResultsPage,
  CampaignsPage,
  NewCampaignPage,
  SheetTemplatesPage,
  MessageSuggestions,
  HubChatPage,
  HubInvitePage,
  HubCompanyInvitePage,
  HubLinkPage,
  HubResumeCode,
  HubResumePhone,
  ChatPage,
  DashboardLayout,
  FidexaHubAuthLayout,
  FidexaHubProtectedRoute,
  ProtectedRoute,
} from "./routes/lazyPages";
import { BackofficeDesignSystemRedirect } from "./backoffice/pages/designSystem/BackofficeDesignSystemRedirect";
import { createBrowserRouter, Navigate } from "react-router";
import { BackofficeRoute } from "./backoffice/components/BackofficeRoute";
import { LegacyQueueDetailRedirect } from "./backoffice/components/hermes/LegacyQueueDetailRedirect";
import { BackofficeRoutes, blogTabPath } from "./backoffice/constants/routes";
import { CampaignRoute } from "./components/campaignGuard/campaignGuard";
import { FidexaPayRoute } from "./components/fidexaPayGuard/fidexaPayGuard";
import { FeatureRoute } from "./components/FeatureGuard";
import { LegacyChatRedirect } from "./components/LegacyChatRedirect";
import { AuthLayout } from "./components/layouts/authLayout";
import { BackofficeRouteError } from "./backoffice/components/layouts/BackofficeRouteError";
import { AppRouteError } from "./components/errorBoundary/appRouteError";
import { RootLayout } from "./components/layouts/rootLayout";
import {
  ChatRoutes,
  DashboardRoutes,
  FidexaHubRoutes,
  PublicRoutes,
} from "./constants/routes";
import { BillingRangeFeatures } from "./constants/billingRangeFeatures";
import { UserManagementFeatures } from "./constants/userManagementFeatures";
import { BillingFailuresRedirect } from "./pages/billingResults/BillingFailuresRedirect";
// Blog no bundle principal, sem lazy: o SSR entrega o post pronto e o SPA
// monta por cima dele. Com chunk sob demanda, o renderizador do Google às
// vezes não baixava o arquivo ("Outro erro"), o React Router mostrava a tela
// de erro e o Search Console recusava o post como "Erro soft 404".
import { BlogAuthorPage } from "./pages/blog/BlogAuthorPage";
import { BlogClusterPage } from "./pages/blog/BlogClusterPage";
import { BlogListPage } from "./pages/blog/BlogListPage";
import { BlogPostPage } from "./pages/blog/BlogPostPage";

const BackofficeLayout = lazyWithReload(() =>
  import("./backoffice/components/layouts/BackofficeLayout").then(m => ({
    default: m.BackofficeLayout,
  }))
);
const BackofficeCompaniesPage = lazyWithReload(() =>
  import("./backoffice/pages/companies/BackofficeCompaniesPage").then(m => ({
    default: m.BackofficeCompaniesPage,
  }))
);
const BackofficeCrmPage = lazyWithReload(() =>
  import("./backoffice/pages/crm/BackofficeCrmPage").then(m => ({
    default: m.BackofficeCrmPage,
  }))
);
const BackofficeCrmCsPage = lazyWithReload(() =>
  import("./backoffice/pages/crm/BackofficeCrmCsPage").then(m => ({
    default: m.BackofficeCrmCsPage,
  }))
);
const BackofficeCrmOperationPage = lazyWithReload(() =>
  import("./backoffice/pages/crm/BackofficeCrmOperationPage").then(m => ({
    default: m.BackofficeCrmOperationPage,
  }))
);
const BackofficePasswordVaultPage = lazyWithReload(() =>
  import("./backoffice/pages/passwordVault/BackofficePasswordVaultPage").then(
    m => ({
      default: m.BackofficePasswordVaultPage,
    })
  )
);
const BackofficeQueuePage = lazyWithReload(() =>
  import("./backoffice/pages/queue/BackofficeQueuePage").then(m => ({
    default: m.BackofficeQueuePage,
  }))
);
const BackofficeMessageManagerPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/message-manager/BackofficeMessageManagerPage"
  ).then(m => ({
    default: m.BackofficeMessageManagerPage,
  }))
);
const BackofficeConciliationPage = lazyWithReload(() =>
  import("./backoffice/pages/conciliation/BackofficeConciliationPage").then(
    m => ({
      default: m.BackofficeConciliationPage,
    })
  )
);
const BackofficeContractsPage = lazyWithReload(() =>
  import("./backoffice/pages/contracts/BackofficeContractsPage").then(m => ({
    default: m.BackofficeContractsPage,
  }))
);
const BackofficeFinanceMetricsPage = lazyWithReload(() =>
  import("./backoffice/pages/financeMetrics/BackofficeFinanceMetricsPage").then(
    m => ({
      default: m.BackofficeFinanceMetricsPage,
    })
  )
);
const BackofficeFinanceApprovalsPage = lazyWithReload(() =>
  import("./backoffice/pages/financeApprovals/BackofficeFinanceApprovalsPage").then(
    m => ({
      default: m.BackofficeFinanceApprovalsPage,
    })
  )
);
const BackofficeMarketingPage = lazyWithReload(() =>
  import("./backoffice/pages/marketing/BackofficeMarketingPage").then(m => ({
    default: m.BackofficeMarketingPage,
  }))
);
const BackofficeMarketingLinkPage = lazyWithReload(() =>
  import("./backoffice/pages/marketing/BackofficeMarketingLinkPage").then(
    m => ({
      default: m.BackofficeMarketingLinkPage,
    })
  )
);
const BackofficeChatsPage = lazyWithReload(() =>
  import("./backoffice/pages/chats/BackofficeChatsPage").then(m => ({
    default: m.BackofficeChatsPage,
  }))
);
const BackofficeBlogPage = lazyWithReload(() =>
  import("./backoffice/pages/blog/BackofficeBlogPage").then(m => ({
    default: m.BackofficeBlogPage,
  }))
);
const BackofficeBlogEditPage = lazyWithReload(() =>
  import("./backoffice/pages/blog/BackofficeBlogEditPage").then(m => ({
    default: m.BackofficeBlogEditPage,
  }))
);
const BackofficeKnowledgeArticlesPage = lazyWithReload(() =>
  import("./backoffice/pages/knowledge/BackofficeKnowledgeArticlesPage").then(
    m => ({
      default: m.BackofficeKnowledgeArticlesPage,
    })
  )
);
const BackofficeKnowledgeArticleEditPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/knowledge/BackofficeKnowledgeArticleEditPage"
  ).then(m => ({
    default: m.BackofficeKnowledgeArticleEditPage,
  }))
);
const BackofficeKnowledgeCategoriesPage = lazyWithReload(() =>
  import("./backoffice/pages/knowledge/BackofficeKnowledgeCategoriesPage").then(
    m => ({
      default: m.BackofficeKnowledgeCategoriesPage,
    })
  )
);
const BackofficeKnowledgeMediaPage = lazyWithReload(() =>
  import("./backoffice/pages/knowledge/BackofficeKnowledgeMediaPage").then(
    m => ({
      default: m.BackofficeKnowledgeMediaPage,
    })
  )
);
const BackofficeKnowledgeInsightsPage = lazyWithReload(() =>
  import("./backoffice/pages/knowledge/BackofficeKnowledgeInsightsPage").then(
    m => ({
      default: m.BackofficeKnowledgeInsightsPage,
    })
  )
);
const BackofficeAiSupportPage = lazyWithReload(() =>
  import("./backoffice/pages/knowledge/BackofficeAiSupportPage").then(m => ({
    default: m.BackofficeAiSupportPage,
  }))
);
const BackofficeHermesMetaAccountsPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesMetaAccountsPage").then(
    m => ({
      default: m.BackofficeHermesMetaAccountsPage,
    })
  )
);
const BackofficeHermesWhatsAppMessagesPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesWhatsAppMessagesPage").then(
    m => ({
      default: m.BackofficeHermesWhatsAppMessagesPage,
    })
  )
);
const BackofficeHermesTemplateLibraryPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesTemplateLibraryPage").then(
    m => ({ default: m.BackofficeHermesTemplateLibraryPage })
  )
);
const WhatsappTemplatesPage = lazyWithReload(() =>
  import("./pages/whatsappTemplates/WhatsappTemplatesPage").then(m => ({
    default: m.WhatsappTemplatesPage,
  }))
);
const WhatsappProfilePage = lazyWithReload(() =>
  import("./pages/whatsappProfile/WhatsappProfilePage").then(m => ({
    default: m.WhatsappProfilePage,
  }))
);
const BackofficeHermesTemplatesPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesTemplatesPage").then(m => ({
    default: m.BackofficeHermesTemplatesPage,
  }))
);
const BackofficeHermesBillingAccountPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesBillingAccountPage").then(
    m => ({ default: m.BackofficeHermesBillingAccountPage })
  )
);
const BackofficeHermesBillingPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesBillingPage").then(m => ({
    default: m.BackofficeHermesBillingPage,
  }))
);
const BackofficeHermesMessagePricesPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesMessagePricesPage").then(
    m => ({
      default: m.BackofficeHermesMessagePricesPage,
    })
  )
);
const BackofficeHermesTemplateMessagesQueuePage = lazyWithReload(() =>
  import(
    "./backoffice/pages/hermes/BackofficeHermesTemplateMessagesQueuePage"
  ).then(m => ({
    default: m.BackofficeHermesTemplateMessagesQueuePage,
  }))
);
const BackofficeHermesTemplateMessageQueueDetailPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/hermes/BackofficeHermesTemplateMessageQueueDetailPage"
  ).then(m => ({
    default: m.BackofficeHermesTemplateMessageQueueDetailPage,
  }))
);
const BackofficeHermesWebhookSearchPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesWebhookSearchPage").then(
    m => ({
      default: m.BackofficeHermesWebhookSearchPage,
    })
  )
);
const BackofficeHermesJobsPage = lazyWithReload(() =>
  import("./backoffice/pages/hermes/BackofficeHermesJobsPage").then(m => ({
    default: m.BackofficeHermesJobsPage,
  }))
);
const BackofficeTemplatesPage = lazyWithReload(() =>
  import("./backoffice/pages/templates/BackofficeTemplatesPage").then(m => ({
    default: m.BackofficeTemplatesPage,
  }))
);
const BackofficeProductUsersPage = lazyWithReload(() =>
  import("./backoffice/pages/productAnalysis/BackofficeProductUsersPage").then(
    m => ({
      default: m.BackofficeProductUsersPage,
    })
  )
);
const BackofficeProductMessagesPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/productAnalysis/BackofficeProductMessagesPage"
  ).then(m => ({
    default: m.BackofficeProductMessagesPage,
  }))
);
const BackofficeProductFidexaPayPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/productAnalysis/BackofficeProductFidexaPayPage"
  ).then(m => ({
    default: m.BackofficeProductFidexaPayPage,
  }))
);
const BackofficeFidexaPayQueuePage = lazyWithReload(() =>
  import("./backoffice/pages/flowPayments/BackofficeFidexaPayQueuePage").then(
    m => ({
      default: m.BackofficeFidexaPayQueuePage,
    })
  )
);
const BackofficeFidexaIaSettingsPage = lazyWithReload(() =>
  import("./backoffice/pages/fidexaIa/BackofficeFidexaIaSettingsPage").then(
    m => ({
      default: m.BackofficeFidexaIaSettingsPage,
    })
  )
);
const BackofficeFidexaIaAuditPage = lazyWithReload(() =>
  import("./backoffice/pages/fidexaIa/BackofficeFidexaIaAuditPage").then(m => ({
    default: m.BackofficeFidexaIaAuditPage,
  }))
);
const BackofficeFidexaIaAgreementsPage = lazyWithReload(() =>
  import("./backoffice/pages/fidexaIa/BackofficeFidexaIaAgreementsPage").then(
    m => ({
      default: m.BackofficeFidexaIaAgreementsPage,
    })
  )
);
const BackofficeFidexaIaCostsPage = lazyWithReload(() =>
  import("./backoffice/pages/fidexaIa/BackofficeFidexaIaCostsPage").then(m => ({
    default: m.BackofficeFidexaIaCostsPage,
  }))
);
const BackofficeFidexaPayAuditPage = lazyWithReload(() =>
  import("./backoffice/pages/flowPayments/BackofficeFidexaPayAuditPage").then(
    m => ({
      default: m.BackofficeFidexaPayAuditPage,
    })
  )
);
const BackofficeBillingResultsPage = lazyWithReload(() =>
  import("./backoffice/pages/billingResults/BackofficeBillingResultsPage").then(
    m => ({
      default: m.BackofficeBillingResultsPage,
    })
  )
);
const BackofficeBillingResultsGroupPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/billingResults/BackofficeBillingResultsGroupPage"
  ).then(m => ({
    default: m.BackofficeBillingResultsGroupPage,
  }))
);
const BackofficeSendWindowPage = lazyWithReload(() =>
  import("./backoffice/pages/sendWindow/BackofficeSendWindowPage").then(m => ({
    default: m.BackofficeSendWindowPage,
  }))
);
const BackofficeScheduleReminderPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/chargeSchedules/BackofficeScheduleReminderPage"
  ).then(m => ({
    default: m.BackofficeScheduleReminderPage,
  }))
);
const BackofficeTemplateProblemsPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/templateProblems/BackofficeTemplateProblemsPage"
  ).then(m => ({
    default: m.BackofficeTemplateProblemsPage,
  }))
);
const BackofficeFlukeNumbersPage = lazyWithReload(() =>
  import("./backoffice/pages/fluke/BackofficeFlukeNumbersPage").then(m => ({
    default: m.BackofficeFlukeNumbersPage,
  }))
);
const BackofficeFeatureFlagsPage = lazyWithReload(() =>
  import("./backoffice/pages/featureFlags/BackofficeFeatureFlagsPage").then(
    m => ({
      default: m.BackofficeFeatureFlagsPage,
    })
  )
);
const BackofficeDashboardBannerPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/dashboardBanner/BackofficeDashboardBannerPage"
  ).then(m => ({
    default: m.BackofficeDashboardBannerPage,
  }))
);
const BackofficeAbTestsPage = lazyWithReload(() =>
  import("./backoffice/pages/abTests/BackofficeAbTestsPage").then(m => ({
    default: m.BackofficeAbTestsPage,
  }))
);
const BackofficeCreditsPage = lazyWithReload(() =>
  import("./backoffice/pages/credits/BackofficeCreditsPage").then(m => ({
    default: m.BackofficeCreditsPage,
  }))
);
const BackofficeLinxIntegrationPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/linxIntegration/BackofficeLinxIntegrationPage"
  ).then(m => ({
    default: m.BackofficeLinxIntegrationPage,
  }))
);
const BackofficeSignInLockPage = lazyWithReload(() =>
  import("./backoffice/pages/signInLock/BackofficeSignInLockPage").then(m => ({
    default: m.BackofficeSignInLockPage,
  }))
);
const BackofficeAuditLogsPage = lazyWithReload(() =>
  import("./backoffice/pages/auditLogs/BackofficeAuditLogsPage").then(m => ({
    default: m.BackofficeAuditLogsPage,
  }))
);
const BackofficeFidexaNotificationsPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/fidexaNotifications/BackofficeFidexaNotificationsPage"
  ).then(m => ({ default: m.BackofficeFidexaNotificationsPage }))
);
const BackofficeGoogleSheetLeadMonitorPage = lazyWithReload(() =>
  import(
    "./backoffice/pages/googleSheetLeadMonitor/BackofficeGoogleSheetLeadMonitorPage"
  ).then(m => ({
    default: m.BackofficeGoogleSheetLeadMonitorPage,
  }))
);
const BackofficeFlowPaymentPage = lazyWithReload(() =>
  import("./backoffice/pages/flowPayments/BackofficeFlowPaymentPage").then(
    m => ({
      default: m.BackofficeFlowPaymentPage,
    })
  )
);
const BackofficeTrainingsPage = lazyWithReload(() =>
  import("./backoffice/pages/trainings/BackofficeTrainingsPage").then(m => ({
    default: m.BackofficeTrainingsPage,
  }))
);
const BackofficeTrainingCommentsPage = lazyWithReload(() =>
  import("./backoffice/pages/trainings/BackofficeTrainingCommentsPage").then(
    m => ({
      default: m.BackofficeTrainingCommentsPage,
    })
  )
);
const BackofficeTrainingRankingPage = lazyWithReload(() =>
  import("./backoffice/pages/trainings/BackofficeTrainingRankingPage").then(
    m => ({
      default: m.BackofficeTrainingRankingPage,
    })
  )
);
const BackofficeTrainingRafflePage = lazyWithReload(() =>
  import("./backoffice/pages/trainings/BackofficeTrainingRafflePage").then(
    m => ({
      default: m.BackofficeTrainingRafflePage,
    })
  )
);
const DesignSystemPublic = lazyWithReload(() =>
  import("./pages/designSystem/designSystemPublic").then(m => ({
    default: m.DesignSystemPublic,
  }))
);

const HermesPage = lazyWithReload(() =>
  import("./hermes/HermesPage").then(m => ({ default: m.HermesPage }))
);

const DemoWelcomePage = lazyWithReload(() =>
  import("./pages/demoWelcome/demoWelcome").then(m => ({
    default: m.DemoWelcome,
  }))
);

const publicRoutes = [
  {
    path: PublicRoutes.HOME,
    element: <LandingPage />,
  },
  {
    path: PublicRoutes.PRIVACY_POLICY,
    element: <PoliticaDePrivacidade />,
  },
  {
    path: PublicRoutes.POLITICA_PRIVACIDADE,
    element: <PoliticaDePrivacidade />,
  },
  {
    path: PublicRoutes.LGPD,
    element: <Lgpd />,
  },
  {
    path: PublicRoutes.RETENCAO_DADOS,
    element: <RetencaoDeDados />,
  },
  {
    path: PublicRoutes.SEGURANCA,
    element: <Seguranca />,
  },
  {
    path: PublicRoutes.DISPONIBILIDADE,
    element: <Disponibilidade />,
  },
  {
    path: PublicRoutes.COMO_CONTAMOS_PAGAMENTOS,
    element: <ComoAFidexaContaPagamentos />,
  },
  {
    path: PublicRoutes.POLITICA_CANCELAMENTO,
    element: <PoliticaDeCancelamento />,
  },
  {
    path: PublicRoutes.OUVIDORIA,
    element: <Ouvidoria />,
  },
  {
    path: PublicRoutes.INCIDENTE_2026_10_16,
    element: <Incidente20261016 />,
  },
  {
    path: PublicRoutes.DESIGN_SYSTEM,
    element: (
      <Suspense fallback={null}>
        <DesignSystemPublic />
      </Suspense>
    ),
  },
  {
    // Mesma página: ela resolve o componente pelo slug. Duas entradas de rota
    // em vez de rota aninhada para não precisar de um segundo Suspense só para
    // trocar de item do índice.
    path: PublicRoutes.DESIGN_SYSTEM_ITEM,
    element: (
      <Suspense fallback={null}>
        <DesignSystemPublic />
      </Suspense>
    ),
  },
  {
    path: PublicRoutes.CONTATO,
    element: <Contato />,
  },
  {
    path: PublicRoutes.TERMS_OF_USE,
    element: <TermosDeUso />,
  },
  {
    path: PublicRoutes.TERMOS_DE_USO,
    element: <TermosDeUso />,
  },
  {
    path: PublicRoutes.CREDIARIO,
    element: <Crediario />,
  },
  {
    path: PublicRoutes.COBRANCA_WHATSAPP,
    element: <CobrancaWhatsapp />,
  },
  {
    path: PublicRoutes.REGUA_COBRANCA,
    element: <ReguaCobranca />,
  },
  {
    path: PublicRoutes.NEWSROOM,
    element: <Newsroom />,
  },
  {
    path: PublicRoutes.INTEGRACOES,
    element: <Integracoes />,
  },
  {
    path: PublicRoutes.FERRAMENTAS,
    element: <Ferramentas />,
  },
  // LPs de SEO (segmento × ferramenta): uma rota por config em
  // src/pages/seoLanding/lps.
  ...SEO_LANDINGS.map(config => ({
    path: config.path,
    element: <SeoLanding config={config} />,
  })),
  {
    path: PublicRoutes.ALPHA7,
    element: <Alpha7 />,
  },
  {
    path: PublicRoutes.FERRAMENTA_GERADOR_CARNE,
    element: <GeradorDeCarne />,
  },
  {
    path: PublicRoutes.FERRAMENTA_SIMULADOR_CREDIARIO,
    element: <SimuladorDeCrediario />,
  },
  {
    path: PublicRoutes.FERRAMENTA_CALCULADORA_INADIMPLENCIA,
    element: <CalculadoraDeInadimplencia />,
  },
  {
    // LP isolada (CSS escopado em .hermes-lp); lazy para não pesar no bundle do app.
    path: PublicRoutes.HERMES,
    element: (
      <Suspense fallback={null}>
        <HermesPage />
      </Suspense>
    ),
  },
  {
    // Para onde o form de demo leva depois do envio. Lazy porque traz o CSS do
    // crachá do /hermes.
    path: PublicRoutes.BOAS_VINDAS,
    element: (
      <Suspense fallback={null}>
        <DemoWelcomePage />
      </Suspense>
    ),
  },
  {
    path: PublicRoutes.BLOG,
    element: (
      <Suspense fallback={null}>
        <BlogListPage />
      </Suspense>
    ),
  },
  {
    // Declarada ANTES de BLOG_POST de propósito: o hub é o padrão mais
    // específico. O react-router ordena as rotas por especificidade antes de
    // casar (segmento estático pontua mais que dinâmico), então hoje
    // "/blog/cluster/x" nunca cairia no post — mas a ordem aqui deixa a
    // intenção explícita e protege a rota se o post um dia virar "/blog/*".
    path: PublicRoutes.BLOG_CLUSTER,
    element: (
      <Suspense fallback={null}>
        <BlogClusterPage />
      </Suspense>
    ),
  },
  {
    // Mesma regra do hub de cluster: três segmentos, declarada ANTES do post.
    path: PublicRoutes.BLOG_AUTHOR,
    element: (
      <Suspense fallback={null}>
        <BlogAuthorPage />
      </Suspense>
    ),
  },
  {
    path: PublicRoutes.BLOG_POST,
    element: (
      <Suspense fallback={null}>
        <BlogPostPage />
      </Suspense>
    ),
  },
  {
    path: PublicRoutes.AUTH,
    element: <AuthLayout />,
    children: [
      {
        path: PublicRoutes.AUTH,
        element: <Auth />,
      },
      {
        path: PublicRoutes.AUTH_OTP,
        element: <OtpLoginPhone />,
      },
      {
        path: PublicRoutes.AUTH_OTP_CODE,
        element: <OtpLoginCode />,
      },
      {
        path: PublicRoutes.AUTH_CHANGE_PASSWORD,
        element: <ChangePassword />,
      },
      {
        path: PublicRoutes.AUTH_FORGOT_PASSWORD,
        element: (
          <Navigate to={PublicRoutes.AUTH_FORGOT_PASSWORD_PHONE} replace />
        ),
      },
      {
        path: PublicRoutes.AUTH_FORGOT_PASSWORD_PHONE,
        element: <ForgotPasswordPhone />,
      },
      {
        path: PublicRoutes.AUTH_FORGOT_PASSWORD_CODE,
        element: <ForgotPasswordCode />,
      },
      {
        path: PublicRoutes.AUTH_FORGOT_PASSWORD_RESET,
        element: <ForgotPasswordReset />,
      },
    ],
  },
  {
    path: FidexaHubRoutes.HUB_LINK,
    element: <FidexaHubAuthLayout />,
    children: [{ index: true, element: <HubLinkPage /> }],
  },
  {
    element: <FidexaHubAuthLayout />,
    children: [
      {
        path: FidexaHubRoutes.HUB_RESUME,
        element: <HubResumePhone />,
      },
      {
        path: FidexaHubRoutes.HUB_RESUME_CODE,
        element: <HubResumeCode />,
      },
      // Não usa FidexaHubProtectedRoute de propósito: HubInvitePage checa
      // autenticação internamente e guarda o token pendente ANTES de
      // redirecionar pro resume (o guard genérico redirecionaria cedo
      // demais, perdendo o token).
      {
        path: FidexaHubRoutes.HUB_INVITE,
        element: <HubInvitePage />,
      },
      // Mesma razão do HUB_INVITE acima: guarda o token pendente ANTES de
      // mandar pro resume, então não pode passar pelo guard genérico.
      {
        path: FidexaHubRoutes.HUB_COMPANY_INVITE,
        element: <HubCompanyInvitePage />,
      },
    ],
  },
  {
    element: <FidexaHubProtectedRoute />,
    children: [
      {
        path: FidexaHubRoutes.HUB_CHAT,
        element: <HubChatPage />,
      },
    ],
  },
];

const privateRoutes = [
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: DashboardRoutes.DASHBOARD,
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Dashboard />,
          },
          {
            path: DashboardRoutes.CUSTOMERS,
            element: <Customers />,
          },
          {
            path: DashboardRoutes.REPORTS_SEND_MESSAGES,
            element: <SendMessages />,
          },
          {
            path: DashboardRoutes.REPORTS_UNRESPONSIVE_CLIENTS,
            element: <UnresponsiveClients />,
          },
          {
            path: DashboardRoutes.REPORTS_RECEIVABLES_PAID,
            element: <ReceivablesPaid />,
          },
          {
            path: DashboardRoutes.REPORTS_SCHEDULED_CLIENT,
            element: <ScheduledClients />,
          },
          {
            path: DashboardRoutes.SETTINGS,
            element: <Settings />,
          },
          {
            // Templates de mensagem virou parte de Faixas de cobranças (Ver/Alterar por faixa).
            path: DashboardRoutes.TEMPLATE_MESSAGE_RECEIVABLES,
            element: (
              <Navigate
                to={`${DashboardRoutes.SETTINGS}?tab=billing`}
                replace
              />
            ),
          },
          {
            path: DashboardRoutes.BILLING_RANGES,
            element: (
              <Navigate
                to={`${DashboardRoutes.SETTINGS}?tab=billing`}
                replace
              />
            ),
          },
          {
            path: DashboardRoutes.WHATSAPP_SETTINGS,
            element: (
              <Navigate
                to={`${DashboardRoutes.SETTINGS}?tab=settings`}
                replace
              />
            ),
          },
          {
            path: DashboardRoutes.CHANGE_PASSWORD,
            element: <DashboardChangePassword />,
          },
          {
            path: DashboardRoutes.USERS,
            element: <FeatureRoute feature={UserManagementFeatures.read} />,
            children: [{ index: true, element: <User /> }],
          },
          {
            path: DashboardRoutes.IMPORT_SHEETS,
            element: <ImportSheets />,
          },
          {
            // Ver os templates não exige permissão; criar sim (dentro da tela).
            path: DashboardRoutes.WHATSAPP_TEMPLATES,
            element: <WhatsappTemplatesPage />,
          },
          {
            // Ver o perfil não exige permissão; editar sim (dentro da tela).
            path: DashboardRoutes.WHATSAPP_PROFILE,
            element: <WhatsappProfilePage />,
          },
          {
            path: DashboardRoutes.BILLING_QUEUE,
            element: <FeatureRoute feature={BillingRangeFeatures.readQueue} />,
            children: [{ index: true, element: <BillingQueuePage /> }],
          },
          {
            path: DashboardRoutes.BILLING_RESULTS,
            element: <FeatureRoute feature={BillingRangeFeatures.readResult} />,
            children: [{ index: true, element: <BillingResultsPage /> }],
          },
          {
            path: DashboardRoutes.BILLING_RESULT_FAILURES,
            element: <FeatureRoute feature={BillingRangeFeatures.readResult} />,
            children: [{ index: true, element: <BillingFailuresRedirect /> }],
          },
          {
            path: DashboardRoutes.BILLING_RESULT_DETAIL,
            element: <FeatureRoute feature={BillingRangeFeatures.readResult} />,
            children: [{ index: true, element: <BillingResultDetailPage /> }],
          },
          // Crediário: só em desenvolvimento enquanto roda com dados de exemplo
          // (o item do menu segue a mesma regra, em routeMetadata).
          ...(import.meta.env.DEV
            ? [
                {
                  path: DashboardRoutes.CREDIT_CUSTOMER,
                  element: <CreditCustomerPage />,
                },
              ]
            : []),
          {
            path: DashboardRoutes.IMPORT_SHEETS_ITEMS,
            element: <FeatureRoute feature="read:import_sheets:report" />,
            children: [{ index: true, element: <ImportSheetsItems /> }],
          },
          {
            // Campanha: gate duplo (permissão + flag por empresa) — ver CampaignRoute.
            path: DashboardRoutes.CAMPAIGNS,
            element: <CampaignRoute feature="read:campaign" />,
            children: [{ index: true, element: <CampaignsPage /> }],
          },
          {
            path: DashboardRoutes.CAMPAIGNS_NEW,
            // Mesma feature da lista: criar campanha é a ação principal da
            // tela, e o backend é quem barra quem não pode disparar.
            element: <CampaignRoute feature="read:campaign" />,
            children: [{ index: true, element: <NewCampaignPage /> }],
          },
          {
            path: DashboardRoutes.CAMPAIGNS_SHEET_TEMPLATES,
            element: <CampaignRoute feature="write:campaign:sheet-template" />,
            children: [{ index: true, element: <SheetTemplatesPage /> }],
          },
          {
            path: DashboardRoutes.CAMPAIGNS_RESULTS,
            element: <CampaignRoute feature="read:campaign:result" />,
            children: [{ index: true, element: <CampaignResultsPage /> }],
          },
          {
            path: DashboardRoutes.CAMPAIGNS_RESULT_DETAIL,
            element: <CampaignRoute feature="read:campaign:result" />,
            children: [{ index: true, element: <CampaignResultDetailPage /> }],
          },
          {
            path: DashboardRoutes.ROADMAP,
            element: <Roadmap />,
          },
          {
            path: DashboardRoutes.TRAINING,
            element: <Training />,
          },
          {
            path: DashboardRoutes.TRAINING_RANKING,
            element: <TrainingRanking />,
          },
          {
            path: DashboardRoutes.TRAINING_DETAIL,
            element: <TrainingDetail />,
          },
          {
            path: DashboardRoutes.MESSAGE_MANAGER,
            element: <MessageManager />,
          },
          {
            path: DashboardRoutes.MESSAGE_SUGGESTIONS,
            element: <MessageSuggestions />,
          },
          {
            path: DashboardRoutes.BILLING_MANAGEMENT,
            element: <BillingManagement />,
          },
          {
            path: DashboardRoutes.REPORTS_CSAT,
            element: <CsatReport />,
          },
          {
            // Fidexa Pay: gate duplo (permissão + flag por loja) — ver
            // FidexaPayRoute. Rota de layout: o guard fica montado entre as telas.
            element: <FidexaPayRoute feature="read:dashboard:home" />,
            children: [
              {
                path: DashboardRoutes.FIDEXA_PAY_ACCOUNT,
                element: <FidexaPayAccount />,
              },
              {
                path: DashboardRoutes.FIDEXA_PAY_SETTINGS,
                element: <FidexaPaySettings />,
              },
              {
                path: DashboardRoutes.FIDEXA_PAY_STATEMENTS,
                element: <FidexaPayStatements />,
              },
              {
                path: DashboardRoutes.FIDEXA_PAY_WITHDRAW,
                element: <FidexaPayWithdraw />,
              },
              {
                path: DashboardRoutes.FIDEXA_PAY_TRANSFER_LEGACY,
                element: (
                  <Navigate to={DashboardRoutes.FIDEXA_PAY_WITHDRAW} replace />
                ),
              },
            ],
          },
        ],
      },
      {
        path: DashboardRoutes.CHAT,
        element: <Navigate to={ChatRoutes.CHAT} replace />,
      },
      {
        path: `${DashboardRoutes.CHAT}/:id`,
        element: <LegacyChatRedirect />,
      },
      {
        path: ChatRoutes.CHAT,
        element: <FeatureRoute feature="read:chat" />,
        children: [{ index: true, element: <ChatPage /> }],
      },
      {
        path: DashboardRoutes.FIDEXA_NOTIFICATION,
        element: <FidexaNotificationPage />,
      },
    ],
  },
  {
    element: <BackofficeRoute />,
    children: [
      {
        path: BackofficeRoutes.ROOT,
        element: (
          <Suspense fallback={null}>
            <BackofficeLayout />
          </Suspense>
        ),
        children: [
          {
            // Tela que quebra ao renderizar: menu e cabeçalho ficam, só o miolo mostra o erro amigável.
            errorElement: <BackofficeRouteError />,
            children: [
              {
                index: true,
                element: <Navigate to={BackofficeRoutes.COMPANIES} replace />,
              },
              {
                path: BackofficeRoutes.COMPANIES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeCompaniesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.MESSAGE_MANAGER,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeMessageManagerPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.CONCILIATION,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeConciliationPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.CONTRACTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeContractsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FINANCE_METRICS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFinanceMetricsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FINANCE_APPROVALS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFinanceApprovalsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.QUEUE,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeQueuePage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.SEND_WINDOW,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeSendWindowPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.TEMPLATE_PROBLEMS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeTemplateProblemsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.SCHEDULE_REMINDER,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeScheduleReminderPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.PASSWORD_VAULT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficePasswordVaultPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.TRAINING,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeTrainingsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.TRAINING_COMMENTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeTrainingCommentsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.TRAINING_RANKING,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeTrainingRankingPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.TRAINING_RAFFLE,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeTrainingRafflePage />
                  </Suspense>
                ),
              },
              {
                // Endereço antigo da vitrine: redireciona para a pública.
                path: BackofficeRoutes.DESIGN_SYSTEM,
                element: <BackofficeDesignSystemRedirect />,
              },
              {
                path: BackofficeRoutes.DESIGN_SYSTEM_ITEM,
                element: <BackofficeDesignSystemRedirect />,
              },
              {
                path: "/backoffice/crm",
                element: <Navigate to={BackofficeRoutes.CRM_LEADS} replace />,
              },
              {
                path: BackofficeRoutes.CRM_LEADS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeCrmPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.CRM_CS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeCrmCsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.CRM_OPERATION,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeCrmOperationPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.MARKETING,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeMarketingPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.MARKETING_LINK,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeMarketingLinkPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.CHATS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeChatsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.BLOG,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeBlogPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.BLOG_LEGACY_CLUSTERS,
                element: <Navigate to={blogTabPath("clusters")} replace />,
              },
              {
                path: BackofficeRoutes.BLOG_LEGACY_TOPICS,
                element: <Navigate to={blogTabPath("pautas")} replace />,
              },
              {
                path: BackofficeRoutes.BLOG_LEGACY_SUGGESTIONS,
                element: <Navigate to={blogTabPath("sugestoes")} replace />,
              },
              {
                path: BackofficeRoutes.BLOG_LEGACY_CMO,
                element: <Navigate to={blogTabPath("cmo")} replace />,
              },
              {
                path: BackofficeRoutes.BLOG_LEGACY_ANALYTICS,
                element: <Navigate to={blogTabPath("analytics")} replace />,
              },
              {
                path: BackofficeRoutes.BLOG_EDIT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeBlogEditPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.KNOWLEDGE_ARTICLES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeKnowledgeArticlesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.KNOWLEDGE_ARTICLE_EDIT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeKnowledgeArticleEditPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.KNOWLEDGE_CATEGORIES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeKnowledgeCategoriesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.KNOWLEDGE_MEDIA,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeKnowledgeMediaPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.KNOWLEDGE_INSIGHTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeKnowledgeInsightsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.KNOWLEDGE_AI_SUPPORT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeAiSupportPage />
                  </Suspense>
                ),
              },
              {
                // "Em breve": liberada só em desenvolvimento; em produção redireciona.
                path: BackofficeRoutes.TEMPLATES,
                element: import.meta.env.DEV ? (
                  <Suspense fallback={null}>
                    <BackofficeTemplatesPage />
                  </Suspense>
                ) : (
                  <Navigate to={BackofficeRoutes.COMPANIES} replace />
                ),
              },
              {
                path: BackofficeRoutes.PRODUCT_ANALYSIS_USERS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeProductUsersPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.PRODUCT_ANALYSIS_MESSAGES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeProductMessagesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.PRODUCT_ANALYSIS_FIDEXA_PAY,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeProductFidexaPayPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.PRODUCT_ANALYSIS_BILLING,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeBillingResultsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.PRODUCT_ANALYSIS_BILLING_GROUP,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeBillingResultsGroupPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FLUKE_NUMBERS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFlukeNumbersPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FEATURE_FLAGS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFeatureFlagsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.DASHBOARD_BANNER,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeDashboardBannerPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.AB_TESTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeAbTestsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.CREDITS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeCreditsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.LINX_INTEGRATION,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeLinxIntegrationPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.SIGN_IN_LOCK,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeSignInLockPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.AUDIT_LOGS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeAuditLogsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FIDEXA_NOTIFICATIONS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaNotificationsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.GOOGLE_SHEET_LEAD_MONITOR,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeGoogleSheetLeadMonitorPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES,
                element: (
                  <Navigate
                    to={BackofficeRoutes.HERMES_META_ACCOUNTS}
                    replace
                  />
                ),
              },
              // Endereços das três telas antigas (business managers, contas e números),
              // hoje unidas em "Contas Meta".
              ...[
                "/backoffice/hermes/business-managers",
                "/backoffice/hermes/whatsapp-business-accounts",
                "/backoffice/hermes/whatsapps",
              ].map(path => ({
                path,
                element: (
                  <Navigate
                    to={BackofficeRoutes.HERMES_META_ACCOUNTS}
                    replace
                  />
                ),
              })),
              {
                path: BackofficeRoutes.HERMES_META_ACCOUNTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesMetaAccountsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_WHATSAPP_MESSAGES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesWhatsAppMessagesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_TEMPLATE_MESSAGES_QUEUE,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesTemplateMessagesQueuePage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_TEMPLATE_MESSAGE_QUEUE_DETAIL,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesTemplateMessageQueueDetailPage />
                  </Suspense>
                ),
              },
              // A Fila de Disparo morava em /whatsapp-messages (e o detalhe em /whatsapp-messages/:id);
              // /whatsapp-messages agora é a lista de Mensagens, então o detalhe antigo redireciona.
              {
                path: "/backoffice/hermes/whatsapp-messages/:id",
                element: <LegacyQueueDetailRedirect />,
              },
              {
                path: BackofficeRoutes.HERMES_TEMPLATES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesTemplatesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_TEMPLATE_LIBRARY,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesTemplateLibraryPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_BILLING_ACCOUNT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesBillingAccountPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_BILLING,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesBillingPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_MESSAGE_PRICES,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesMessagePricesPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_WEBHOOK_SEARCH,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesWebhookSearchPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.HERMES_JOBS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeHermesJobsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FLOW_PAYMENTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFlowPaymentPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FLOW_PAYMENTS_AUDIT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaPayAuditPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FLOW_PAYMENTS_QUEUE,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaPayQueuePage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FIDEXA_IA,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaIaSettingsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FIDEXA_IA_AUDIT,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaIaAuditPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FIDEXA_IA_AGREEMENTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaIaAgreementsPage />
                  </Suspense>
                ),
              },
              {
                path: BackofficeRoutes.FIDEXA_IA_COSTS,
                element: (
                  <Suspense fallback={null}>
                    <BackofficeFidexaIaCostsPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <AppRouteError />,
    children: [
      ...publicRoutes,
      ...privateRoutes,
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);
